import { useMemo, useCallback } from "react";
import type { BaseEntity } from "../../../types";
import type { BulkActionWithCallback } from "../../../../BulkActions/types";

interface MemoizationResult<TData extends BaseEntity> {
  selectedItemsArray: TData[];
  allItemIds: string[];
  handleClearSelection: () => void;
  handleSelectAllItems: () => void;
  customActions: BulkActionWithCallback<TData>[];
}

/**
 * Handles all memoization logic for SimpleTableDesktopView
 * Prevents unnecessary re-renders by memoizing computed values and callbacks
 */
export function useSimpleTableMemoization<TData extends BaseEntity>(
  paginatedData: TData[],
  selectedItems: Set<string>,
  setSelectedItems: (
    items: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void,
  config: any,
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void
): MemoizationResult<TData> {
  // Memoize selected items array to prevent recreation on every render
  const selectedItemsArray = useMemo(() => {
    return paginatedData.filter((item) => selectedItems.has(item.id));
  }, [paginatedData, selectedItems]);

  // Memoize all item IDs array
  const allItemIds = useMemo(() => {
    return paginatedData.map((item) => item.id);
  }, [paginatedData]);

  // Memoize clear selection handler
  const handleClearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, [setSelectedItems]);

  // Memoize select all handler
  const handleSelectAllItems = useCallback(() => {
    setSelectedItems(new Set(allItemIds));
  }, [setSelectedItems, allItemIds]);

  // Memoize custom actions to prevent recreation on every render
  const customActions = useMemo(() => {
    const actions =
      config.bulkActions?.map((action: any) => {
        // Check if this action has an onClick handler (custom action)
        const actionWithCallback = action;
        if (actionWithCallback.onClick) {
          // Custom action with onClick - use it directly
          return {
            id: action.id,
            label: action.label,
            icon: action.icon || "⚙️", // Provide default icon if not specified
            variant: action.variant,
            onClick: actionWithCallback.onClick, // Use the custom onClick directly
          };
        }
        // Standard action - wrap with onBulkAction
        return {
          id: action.id,
          label: action.label,
          icon: action.icon || "⚙️", // Provide default icon if not specified
          onClick: async (selectedItems: TData[], selectedIds: string[]) => {
            onBulkAction?.(action.id, selectedItems);
          },
        };
      }) || [];
    console.log("[BULK_ACTION] useSimpleTableMemoization customActions created:", {
      count: actions.length,
      actions: actions.map((a: BulkActionWithCallback<TData>) => ({ id: a.id, label: a.label })),
    });
    return actions;
  }, [config.bulkActions, onBulkAction]) as BulkActionWithCallback<TData>[];

  return {
    selectedItemsArray,
    allItemIds,
    handleClearSelection,
    handleSelectAllItems,
    customActions,
  };
}
